import { inject, Injectable } from '@angular/core';
import { Router, UrlSegmentGroup, UrlTree } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';
import { LocalStorageService } from 'ngx-webstorage';
import { EnvironmentService } from '@irian-ro/util-environment';
import { Observable, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { cookieTokenName, localStorageTokenName } from '@irian-ro/util-user';
import { Cookies } from 'typescript-cookie';
import { createAuthConfig } from './auth-config';
import {
  LoginErrorCode,
  OIDC_BLOCKED_MULTI_SEGMENTS,
  OIDC_BLOCKED_QUERY_PARAMS,
  OIDC_BLOCKED_ROUTES,
  OIDC_BLOCKED_SEGMENTS,
} from './auth-routes';

@Injectable({ providedIn: 'root' })
export class OidcService {
  private static readonly LOGIN_WINDOW_MS = 60_000;
  private static readonly LOGIN_MAX_ATTEMPTS = 5;
  private static readonly LOCKOUT_MS = 5 * 60_000;
  private static readonly LS_ATTEMPTS = 'oidc_login_attempts';
  private static readonly LS_WINDOW = 'oidc_login_window_started_at';
  private static readonly LS_LOCKED_UNTIL = 'oidc_locked_until';
  private static readonly SS_RETURN_URL = 'oidc_return_url';
  private static readonly SS_LOGOUT_PENDING = 'oidc_logout_pending';

  private oauthService = inject(OAuthService);
  private localStorage = inject(LocalStorageService);
  private environmentService = inject(EnvironmentService);
  private router = inject(Router);
  private initialized = false;
  private tokenExpired$ = new Subject<void>();
  // One-shot guard against re-entrant logout. The lib's own session monitoring
  // can synchronously emit `session_terminated` while we're already inside a
  // logout-driven navigation; without this guard, the resulting recursive call
  // races a second `window.location.href` assignment against our own and the
  // user lands on `?error=session_ended` without actually logging out from the
  // IdP. Never reset — the page is navigating away.
  private logoutInProgress = false;

  constructor() {
    this.subscribeToTokenEvents();
  }

  get onTokenExpired(): Observable<void> {
    return this.tokenExpired$.asObservable();
  }

  get onAuthenticated(): Observable<void> {
    return this.oauthService.events.pipe(
      filter(e => e.type === 'token_received'),
      map(() => undefined as void)
    );
  }

  /** Configure and prepare the library. Call once at app startup. */
  init(): void {
    if (this.initialized) return;
    const oidcEnv = this.environmentService.getEnvironmentProperty('oidc');
    if (!oidcEnv) return;
    this.oauthService.configure(createAuthConfig(oidcEnv));
    this.initialized = true;
  }

  /** Restore session after page reload — re-arm silent refresh if tokens exist. */
  tryRestoreSession(): void {
    this.init();
    if (this.oauthService.hasValidAccessToken()) {
      this.setupAutoRefresh();
      this.syncTokensToLegacyStorage();
    } else {
      this.clearLegacyStorage();
    }
  }

  /**
   * Start the OIDC login redirect. Sanitises the requested returnUrl at the WRITE site —
   * any value that points at an auth route, the callback, or carries OIDC artefacts is
   * coerced to '/'. Trips a circuit-breaker on the LOGIN_MAX_ATTEMPTS-th call within
   * LOGIN_WINDOW_MS and applies a 5-minute hard lockout. The breaker counter is reset on
   * a real token_received (see subscribeToTokenEvents), so a successful auth between
   * attempts does not poison the next retry.
   */
  login(returnUrl?: string): void {
    this.init();
    if (this.isLoopLockoutActive()) {
      window.location.replace(this.buildLoginErrorUrl('loop_detected'));
      return;
    }
    const safe = this.sanitizeReturnUrl(returnUrl);
    if (!this.recordLoginAttempt()) return;
    sessionStorage.setItem(OidcService.SS_RETURN_URL, safe);
    this.oauthService.initCodeFlow();
  }

  /**
   * Build the absolute URL of the login page with an `?error=` code, honouring the
   * deployed base href via `oidc.postLogoutRedirectUri` (which is fully qualified per
   * environment). Falls back to `/app/auth/login` only when env config is missing.
   * Used by all `window.location.*` exits — these bypass Angular's Router and would
   * otherwise hit the wrong path under a non-`/` base href. Public so terminal-state
   * components (e.g. `LoginCallbackComponent.failOut`) can use a full-page navigation
   * consistently with the breaker/logout exits inside this service.
   *
   * `returnUrl`, when provided and non-trivial, is appended URL-encoded so the retry
   * flow can land the user back where they were going. The login page is responsible
   * for re-validating any returnUrl it consumes — defence in depth at the read site.
   */
  buildLoginErrorUrl(errorCode: LoginErrorCode, returnUrl?: string | null): string {
    const oidcEnv = this.environmentService.getEnvironmentProperty('oidc');
    const base: string = oidcEnv?.postLogoutRedirectUri || '/app/auth/login';
    let url = `${base}${base.indexOf('?') !== -1 ? '&' : '?'}error=${errorCode}`;
    if (returnUrl && returnUrl !== '/') {
      url += `&returnUrl=${encodeURIComponent(returnUrl)}`;
    }
    return url;
  }

  /** Exchange the authorization code from the callback URL for tokens. Returns a Promise. */
  async loginCallbackInit(): Promise<void> {
    this.init();
    await this.oauthService.tryLogin();
    this.syncTokensToLegacyStorage();
  }

  get accessToken(): string | null {
    return this.oauthService.getAccessToken();
  }

  get identityClaims(): Record<string, any> | null {
    const idClaims = this.oauthService.getIdentityClaims();
    if (idClaims) return idClaims;
    const token = this.accessToken;
    if (!token) return null;
    return this.decodeJwt(token);
  }

  get isAuthenticated(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  /**
   * Terminate the user session. When an id token is present we redirect through the IdP's
   * end_session_endpoint (which kills the IdP SSO cookie); otherwise we clear local state
   * and bounce to /auth/login with `?error=session_ended`.
   *
   * In BOTH branches we set the `oidc_logout_pending` sessionStorage flag before any
   * navigation. Reason: the IdP-side logout redirect lands on `postLogoutRedirectUri`
   * (= `/app/auth/login`) WITHOUT any query params we control, so without this flag
   * `LoginComponent.ngOnInit` would auto-fire `oidcService.login('/')` and — if the IdP
   * SSO cookie survived (RP-initiated logout disabled or misconfigured) — silently
   * re-authenticate the user immediately after they explicitly logged out. The login
   * page consumes the flag exactly once and renders the `session_ended` banner.
   *
   * The id-token check MUST happen before `clearLegacyStorage()` — the lib uses the id
   * token as `id_token_hint` when building the end_session_endpoint redirect, but
   * crucially it reads it from its own OAuthStorage (which we DO NOT touch here), so
   * clearing the legacy mirror first is safe. We still order it deliberately to keep
   * the contract obvious to readers.
   */
  logout(): void {
    if (this.logoutInProgress) return;
    this.logoutInProgress = true;
    this.clearLoopState();
    this.markLogoutPending();

    if (this.oauthService.hasValidIdToken()) {
      // Lib's logOut() clears its own OAuthStorage and redirects to end_session_endpoint.
      // We only need to clear the legacy mirror (cookies + ngx-webstorage).
      this.clearLegacyStorage();
      this.oauthService.logOut();
      return;
    }

    // No id token — local-only logout, then land on /auth/login with the banner.
    this.clearAllAuthState();
    window.location.href = this.buildLoginErrorUrl('session_ended');
  }

  private markLogoutPending(): void {
    try {
      sessionStorage.setItem(OidcService.SS_LOGOUT_PENDING, '1');
    } catch {
      // sessionStorage unavailable (private mode/sandbox) — the URL-based ?error=
      // session_ended fallback in the no-id-token branch still works; the IdP-redirect
      // branch loses the safety net, which is the same behaviour as before this fix.
    }
  }

  /**
   * Check whether this tab is in the "user explicitly logged out" state. Unlike
   * `consumeLogoutPending()`, this leaves the flag in place so the login page keeps
   * suppressing auto-login across refreshes until the user explicitly retries or a
   * real `token_received` event clears the intent.
   */
  hasLogoutPending(): boolean {
    try {
      return !!sessionStorage.getItem(OidcService.SS_LOGOUT_PENDING);
    } catch {
      return false;
    }
  }

  /**
   * Clear the logout-pending flag after an explicit user action (e.g. clicking
   * "Try again") or any other flow that should re-enable auto-login.
   */
  clearLogoutPending(): void {
    try {
      sessionStorage.removeItem(OidcService.SS_LOGOUT_PENDING);
    } catch {
      // ignore
    }
  }

  /**
   * Returns true (and clears the flag) iff the user just logged out and we want the
   * login page to short-circuit its auto-login. Called from `LoginComponent.ngOnInit`
   * before any other logic. Must be idempotent — repeated calls within the same tab
   * after consumption return false.
   */
  consumeLogoutPending(): boolean {
    try {
      if (sessionStorage.getItem(OidcService.SS_LOGOUT_PENDING)) {
        sessionStorage.removeItem(OidcService.SS_LOGOUT_PENDING);
        return true;
      }
    } catch {
      // ignore — fail open (auto-login behaves as it did before this flag existed)
    }
    return false;
  }

  /** Wipe every place the app keeps auth state: lib OAuthStorage, ngx-webstorage mirror, cookies. */
  clearAllAuthState(): void {
    try {
      this.oauthService.logOut(true);
    } catch {
      // Boot path may not have configured the lib yet — safe to ignore.
    }
    this.clearLegacyStorage();
  }

  /** Re-sanitise `oidc_return_url` in place. Idempotent. Used by APP_INITIALIZER and on entry. */
  sanitizeStoredReturnUrl(): void {
    this.init();
    const stored = sessionStorage.getItem(OidcService.SS_RETURN_URL);
    if (!stored) return;
    const safe = this.sanitizeReturnUrl(stored);
    if (safe === '/') {
      sessionStorage.removeItem(OidcService.SS_RETURN_URL);
    } else if (safe !== stored) {
      sessionStorage.setItem(OidcService.SS_RETURN_URL, safe);
    }
  }

  /** Start automatic token refresh (call after successful login) */
  setupAutoRefresh(): void {
    this.oauthService.setupAutomaticSilentRefresh();
  }

  /**
   * Reject return URLs that would re-trigger OIDC, log the user out, leak external origins,
   * or carry OIDC artefacts. Same-origin paths only; the URL is parsed with `Router.parseUrl`
   * and every segment in every outlet is walked. Multiple decoders defeat `%2F`-style
   * single-encoding bypasses.
   */
  private sanitizeReturnUrl(url: string | undefined): string {
    if (!url) return '/';

    let parsed: URL;
    try {
      parsed = new URL(url, window.location.origin);
    } catch {
      return '/';
    }
    if (parsed.origin !== window.location.origin) return '/';

    const blockedQ = OIDC_BLOCKED_QUERY_PARAMS as readonly string[];
    if (this.queryHasBlockedKey(parsed.search, blockedQ)) return '/';

    if (/(?:^|[#&?])(code|state|session_state|access_token|id_token|token)=/i.test(parsed.hash)) {
      return '/';
    }

    let tree: UrlTree;
    try {
      tree = this.router.parseUrl(parsed.pathname + parsed.search);
    } catch {
      return '/';
    }
    if (this.containsBlockedSegment(tree.root)) return '/';

    let decodedPath: string;
    try {
      decodedPath = decodeURIComponent(parsed.pathname);
    } catch {
      return '/';
    }
    try {
      decodedPath = decodeURIComponent(decodedPath);
    } catch {
      // Already fully decoded — fine.
    }
    decodedPath = decodedPath.replace(/\\/g, '/');

    for (let i = 0; i < OIDC_BLOCKED_ROUTES.length; i++) {
      const p = OIDC_BLOCKED_ROUTES[i];
      if (decodedPath === p || decodedPath.indexOf(p + '/') === 0) return '/';
    }

    for (let i = 0; i < OIDC_BLOCKED_MULTI_SEGMENTS.length; i++) {
      if (decodedPath.indexOf(OIDC_BLOCKED_MULTI_SEGMENTS[i]) !== -1) return '/';
    }

    return parsed.pathname + parsed.search + parsed.hash;
  }

  /**
   * Walk a `?` query string for any case-insensitive match against blocked names. We avoid
   * `URLSearchParams.keys()` because it requires the `dom.iterable` TS lib which not all
   * tsconfigs in this repo enable.
   */
  private queryHasBlockedKey(search: string, blocked: readonly string[]): boolean {
    if (!search) return false;
    const raw = search.startsWith('?') ? search.slice(1) : search;
    if (!raw) return false;
    for (const pair of raw.split('&')) {
      if (!pair) continue;
      const eqIdx = pair.indexOf('=');
      const rawKey = eqIdx === -1 ? pair : pair.substring(0, eqIdx);
      let key: string;
      try {
        key = decodeURIComponent(rawKey).toLowerCase();
      } catch {
        key = rawKey.toLowerCase();
      }
      if (blocked.indexOf(key) !== -1) return true;
    }
    return false;
  }

  private containsBlockedSegment(group: UrlSegmentGroup): boolean {
    const blockedSeg = OIDC_BLOCKED_SEGMENTS as readonly string[];
    const blockedQ = OIDC_BLOCKED_QUERY_PARAMS as readonly string[];
    for (const seg of group.segments) {
      if (blockedSeg.indexOf(seg.path.toLowerCase()) !== -1) return true;
      const paramKeys = Object.keys(seg.parameters);
      for (let i = 0; i < paramKeys.length; i++) {
        if (blockedQ.indexOf(paramKeys[i].toLowerCase()) !== -1) return true;
      }
    }
    const childKeys = Object.keys(group.children);
    for (let i = 0; i < childKeys.length; i++) {
      if (this.containsBlockedSegment(group.children[childKeys[i]])) return true;
    }
    return false;
  }

  /**
   * Counter circuit-breaker. Counts attempts in a rolling LOGIN_WINDOW_MS window; on the
   * LOGIN_MAX_ATTEMPTS-th call within the window, sets a 5-minute hard lockout and aborts
   * via location.replace. Counter lives in localStorage so it is shared across tabs (the
   * lib's own auth state lives in localStorage too — keeping the breaker per-tab would let
   * a multi-tab loop burn N full cycles per tab before tripping).
   *
   * Returns false when the caller MUST NOT proceed with initCodeFlow (locked out, or
   * localStorage unavailable). Does not throw — callers (lifecycle hooks, guards,
   * setTimeouts) can't reliably handle a synchronous throw across the location.replace.
   */
  private recordLoginAttempt(): boolean {
    let attempts = 0;
    let windowStart = 0;
    try {
      const now = Date.now();
      windowStart = Number(localStorage.getItem(OidcService.LS_WINDOW) || 0);
      attempts = Number(localStorage.getItem(OidcService.LS_ATTEMPTS) || 0);

      if (!windowStart || now - windowStart > OidcService.LOGIN_WINDOW_MS) {
        localStorage.setItem(OidcService.LS_WINDOW, String(now));
        attempts = 0;
      }
      attempts += 1;
      localStorage.setItem(OidcService.LS_ATTEMPTS, String(attempts));
    } catch {
      // localStorage unavailable (private mode, quota, sandbox) — fail closed.
      console.error('[oidc] localStorage unavailable; refusing login() to avoid uncountable loop');
      window.location.replace(this.buildLoginErrorUrl('loop_detected'));
      return false;
    }

    if (attempts >= OidcService.LOGIN_MAX_ATTEMPTS) {
      console.error('[oidc] login() called', attempts, 'times in', OidcService.LOGIN_WINDOW_MS, 'ms — aborting');
      // Panic-reset every form of auth state (lib OAuthStorage, legacy mirror,
      // cookies, sessionStorage flags, breaker counters) so the user lands on
      // the error page with a clean slate. Without this, residual tokens or a
      // stale `oidc_return_url` would put the user one click away from the
      // exact same broken state they just escaped from. The lockout marker is
      // planted AFTER the wipe so it survives — that's the brake that prevents
      // an immediate re-loop until the user explicitly hits "Try again".
      this.panicResetAuthState();
      try {
        localStorage.setItem(OidcService.LS_LOCKED_UNTIL, String(Date.now() + OidcService.LOCKOUT_MS));
      } catch {
        // ignore
      }
      window.location.replace(this.buildLoginErrorUrl('loop_detected'));
      return false;
    }
    return true;
  }

  /**
   * Wipe every persisted form of auth state EXCEPT the lockout marker. Called
   * from the breaker trip path so the user does not land on the error page
   * still carrying the broken state that caused the loop. Order matters:
   *  1. lib OAuthStorage (oauth_*) — `oauthService.logOut(true)` (no redirect)
   *  2. legacy mirror in ngx-webstorage + cookies — `clearLegacyStorage`
   *  3. our own session-scoped flags (`oidc_return_url`, `oidc_logout_pending`)
   *  4. breaker counters (`oidc_login_attempts`, `oidc_login_window_started_at`)
   *
   * `oidc_locked_until` is deliberately NOT cleared — the trip path plants it
   * RIGHT AFTER calling this and relies on it to stop the immediate re-loop
   * via the `isLoopLockoutActive()` check at the top of `login()`. The user
   * releases it explicitly via `recoverFromLoopDetection()` (the "Try again"
   * button on the error page) or by waiting out the 5-minute lockout window.
   */
  private panicResetAuthState(): void {
    // Suppress re-entrant logout. `oauthService.logOut(true)` synchronously
    // emits `session_terminated`, which our own `subscribeToTokenEvents`
    // listener forwards to `tokenExpired$`. The shell's app.component
    // subscribes to that and would call `oidcService.logout()` from inside
    // this stack frame — and a real logout would race a second
    // `window.location.href` against the breaker's `window.location.replace`
    // (different `?error=` codes, last-call-wins is fragile). Setting the
    // guard upfront makes the re-entrant `logout()` early-return at its top.
    // We deliberately do NOT reset the guard: the breaker is about to call
    // `window.location.replace`, the page is navigating away — same contract
    // as the real logout path, which also never resets the flag.
    this.logoutInProgress = true;
    try {
      this.oauthService.logOut(true);
    } catch {
      // Lib may not be configured on the boot path — best-effort wipe.
    }
    this.clearLegacyStorage();
    try {
      sessionStorage.removeItem(OidcService.SS_RETURN_URL);
      sessionStorage.removeItem(OidcService.SS_LOGOUT_PENDING);
    } catch {
      // ignore
    }
    this.clearLoopBreakerCounters();
  }

  /**
   * Release the cross-tab lockout AND any residual breaker counters so a
   * subsequent `login()` call can proceed. The error-page "Try again" handler
   * calls this immediately before re-entering the login flow — without it,
   * `login()` would early-return via `isLoopLockoutActive()` and bounce the
   * page right back to `?error=loop_detected`.
   */
  recoverFromLoopDetection(): void {
    this.clearLoopLockout();
    this.clearLoopBreakerCounters();
  }

  private isLoopLockoutActive(): boolean {
    try {
      const until = Number(localStorage.getItem(OidcService.LS_LOCKED_UNTIL) || 0);
      if (until > Date.now()) return true;
      // Self-clean expired/invalid value so it doesn't linger across sessions.
      if (until > 0) localStorage.removeItem(OidcService.LS_LOCKED_UNTIL);
      return false;
    } catch {
      return false;
    }
  }

  /**
   * Reset the rolling-window breaker counters only. Does NOT touch `oidc_return_url`
   * or `LS_LOCKED_UNTIL`. Used by the `token_received` listener — that fires synchronously
   * inside the lib's `tryLogin()` call, BEFORE `LoginCallbackComponent` has had a chance
   * to read the stored return URL, so wiping it here would break every deep-linked login.
   * The lockout flag is managed separately (see `clearLoopLockout`) because this helper is
   * also called from `clearLoopState`, which the trip path invokes AFTER setting the
   * lockout — including LS_LOCKED_UNTIL here would have it undo its own lockout.
   */
  private clearLoopBreakerCounters(): void {
    try {
      localStorage.removeItem(OidcService.LS_ATTEMPTS);
      localStorage.removeItem(OidcService.LS_WINDOW);
    } catch {
      // ignore
    }
  }

  /**
   * Release the cross-tab hard lockout. A real `token_received` event proves we are not
   * in a loop, and a successful auth in any tab should release the shared lockout —
   * otherwise the user stays locked out for 5 min in this tab even though they have a
   * valid session elsewhere. Kept separate from `clearLoopBreakerCounters` so the
   * lockout-trip path (which calls `clearLoopState` after planting the lockout) doesn't
   * undo its own work.
   */
  private clearLoopLockout(): void {
    try {
      localStorage.removeItem(OidcService.LS_LOCKED_UNTIL);
    } catch {
      // ignore
    }
  }

  /**
   * Full reset: breaker counters + the stored return URL. Used at logout and on
   * lockout trip — both terminal states where any pending return URL is moot.
   * NOT to be used from token-event listeners; see `clearLoopBreakerCounters`.
   */
  private clearLoopState(): void {
    sessionStorage.removeItem(OidcService.SS_RETURN_URL);
    this.clearLoopBreakerCounters();
  }

  private subscribeToTokenEvents(): void {
    this.oauthService.events
      .pipe(filter(e => e.type === 'token_received' || e.type === 'token_refreshed'))
      .subscribe(() => {
        this.syncTokensToLegacyStorage();
        // A real token issuance is, by definition, not a loop. Reset the breaker so
        // legitimate flurries (logout-then-login, multi-tab cold start, sleep-wake)
        // don't compound into a false-positive lockout. We deliberately do NOT clear
        // `oidc_return_url` here — it is read AFTER this listener runs by the
        // login-callback's user-fetch success handler.
        this.clearLoopBreakerCounters();
        // Also release any active lockout — a real token issuance proves the user is
        // not in a loop. Cross-tab: this lets a successful auth in one tab unlock
        // every other tab, instead of leaving them stranded for the full 5 min.
        this.clearLoopLockout();
        // A successful auth invalidates any pending logout intent: the user has chosen
        // to be signed in. Without this, a logout-then-back-button-to-guarded-page flow
        // (which re-authenticates without traversing /auth/login) would leave the flag
        // dangling and surface a stale `session_ended` banner the next time the user
        // landed on the login page.
        try {
          sessionStorage.removeItem(OidcService.SS_LOGOUT_PENDING);
        } catch {
          // ignore
        }
      });

    this.oauthService.events
      .pipe(filter(e =>
        e.type === 'token_expires' ||
        e.type === 'session_terminated' ||
        e.type === 'token_refresh_error' ||
        e.type === 'token_error'
      ))
      .subscribe(() => {
        if (!this.oauthService.hasValidAccessToken()) {
          this.clearLegacyStorage();
          this.tokenExpired$.next();
        }
      });
  }

  /** @deprecated Will be removed when all consumers read from OidcTokenService */
  private syncTokensToLegacyStorage(): void {
    const token = this.oauthService.getAccessToken();
    if (!token) return;

    this.localStorage.store(localStorageTokenName, token);

    const claims = this.decodeJwt(token);
    if (claims?.owner) {
      this.localStorage.store('owner', claims.owner);
      Cookies?.set('organization', claims.owner);
    }

    Cookies?.set(cookieTokenName, token);
  }

  private clearLegacyStorage(): void {
    this.localStorage.clear(localStorageTokenName);
    this.localStorage.clear('owner');
    this.localStorage.clear('refreshToken');
    Cookies?.remove(cookieTokenName);
    Cookies?.remove('organization');
  }

  private decodeJwt(token: string): any {
    try {
      const payload = token.split('.')[1];
      return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    } catch {
      return null;
    }
  }
}
